/**
 * CrewParticipantWelcome - participant-facing greeting for production invitees.
 * Personal greeting, welcome video, whole-process framing, data notice.
 */

import React, { useRef, useState } from 'react';
import { crewFirstName } from '../../utils/participantCrewContext';

const WELCOME_VIDEO_SRC = '/videos/participant-welcome.mp4';

export default function CrewParticipantWelcome({ context, compact = false }) {
  const videoRef = useRef(null);
  const [videoStarted, setVideoStarted] = useState(false);
  if (!context?.name && !context?.projectName) return null;

  const firstName = crewFirstName(context.name) || 'there';
  const production = context.projectName || 'your production';
  const cardTone = compact
    ? 'border-white/10 bg-[#0f2237] text-slate-100'
    : 'border-emerald-200 bg-gradient-to-br from-emerald-50 to-white';
  const subtitleTone = compact ? 'text-slate-300' : 'text-slate-600';
  const noticeTone = compact
    ? 'text-slate-300 bg-white/5 border-white/10'
    : 'text-slate-600 bg-white/80 border-slate-100';
  const chipTone = compact
    ? 'bg-white/5 border-white/15 text-slate-200'
    : 'bg-white border-slate-200 text-slate-600';

  return (
    <div className={`rounded-2xl border ${cardTone} ${compact ? 'p-4 mb-4' : 'p-5 mb-6'}`}>
      <p className="text-xs font-bold uppercase tracking-wider text-emerald-400 mb-1">Participant workbench</p>
      <h2 className={`text-lg font-bold ${compact ? 'text-white' : 'text-slate-900'}`}>
        Hi <span className="text-emerald-700">{firstName}</span>
        {context.projectName ? (
          <> - <span className="font-semibold">{production}</span></>
        ) : null}
      </h2>
      <p className={`text-sm mt-2 leading-relaxed ${subtitleTone}`}>
        Your producer already imported your details from the schedule. Confirm what we have, add what is missing,
        and upload documents - your immigration assistant guides you through the whole process.
      </p>

      {!compact ? (
        <div className="mt-4 rounded-xl overflow-hidden border border-slate-200 bg-slate-900/95 relative">
          <video
            ref={videoRef}
            src={WELCOME_VIDEO_SRC}
            className="w-full aspect-video object-cover bg-black"
            controls={videoStarted}
            playsInline
            preload="metadata"
            onPlay={() => setVideoStarted(true)}
          />
          {!videoStarted ? (
            <button
              type="button"
              onClick={() => {
                setVideoStarted(true);
                videoRef.current?.play();
              }}
              className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white bg-slate-900/60 hover:bg-slate-800/70 transition-colors"
              aria-label="Play welcome video"
            >
              <span className="w-12 h-12 rounded-full bg-white/15 flex items-center justify-center">
                <i className="ri-play-fill text-2xl" aria-hidden />
              </span>
              <span className="text-sm font-semibold">Watch a short welcome (2 min)</span>
            </button>
          ) : null}
        </div>
      ) : null}

      <div className={`mt-4 flex items-start gap-2 text-xs rounded-xl p-3 ${noticeTone}`}>
        <i className="ri-shield-user-line text-emerald-600 mt-0.5 shrink-0" aria-hidden />
        <p>
          {context.dataNotice || (
            <>
              We use your name, email, role, nationality, and schedule from the production import to run your
              immigration case. You will confirm this information as you go.
            </>
          )}
        </p>
      </div>

      {(context.jobTitle || context.country) && (
        <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
          {context.jobTitle ? (
            <span className={`px-2 py-1 rounded-lg border ${chipTone}`}>
              <i className="ri-briefcase-line mr-1" aria-hidden />{context.jobTitle}
            </span>
          ) : null}
          {context.country ? (
            <span className={`px-2 py-1 rounded-lg border ${chipTone}`}>
              <i className="ri-global-line mr-1" aria-hidden />{context.country}
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
}
